import { gql, useLazyQuery, useMutation } from '@apollo/client'
import { Mutation, Query, Retailer } from '@promoboxx/graphql-gateway-types'
import useFilter from '@promoboxx/use-filter'
import I18n from 'i18n-js'
import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'

import urlParamStore from '@src/lib/urlParamStore'
import { useFeatureFlag } from '@src/lib/useFeatureFlag'
import useOnRetailerAccountChange from '@src/lib/useOnRetailerAccountChange'
import usePreviouslySelectedBrand from '@src/lib/usePreviouslySelectedBrand'

import { currentRetailerAccountSelector } from '../../stores/app/selectors'
import { currentRetailerIdSelector } from '../../stores/retailers/selectors'
import PlatformAvatarGroup from '../reporting/PlatformAvatarGroup'

import {
  CampaignCardActionAreaDot,
  CampaignCardTruncatedDescription,
} from './CampaignCard'
import CampaignResultsContainer from './CampaignResultsContainer'
import ContentFilters from './ContentFilters'
import { OmniCampaignCardProps } from './OmniCampaignCard'
import organicCampaignPaths from './organicCampaignPaths'

export const CONTENT_DISCOVERY = gql`
  query ContentDiscoveryContentPage(
    $resourceType: String!
    $resourceId: ID!
    $params: ContentDiscoveryParams
  ) {
    getContentDiscovery(
      resourceType: $resourceType
      resourceId: $resourceId
      params: $params
    ) {
      data {
        id
        brand_id
        brand {
          id
          logo_url
          slug
        }
        post_id
        content_hash
        channel_source
        channel_type
        content_type
        message
        title
        description
        content_created_at
        image_url
        video_url
        url
        enabled
        created_at
        update_at
      }
      paging {
        total_results
      }
    }
  }
`

const CREATE_CONTENT_DISCOVERY_CAMPAIGN = gql`
  mutation CreateContentDiscoveryCampaign(
    $contentDiscoveryId: ID!
    $retailerId: ID!
  ) {
    createContentDiscoveryCampaign(
      retailerId: $retailerId
      contentDiscoveryId: $contentDiscoveryId
    ) {
      id
      creative_path
    }
  }
`

const CampaignContentDiscovery: React.FC<
  React.PropsWithChildren<{
    brandFilter?: string
    filterBarRef: HTMLDivElement | null
  }>
> = ({ filterBarRef, brandFilter }) => {
  const pageSize = 24
  const retailerId = useSelector(currentRetailerIdSelector)
  const [campaignCards, setCampaignCards] = useState<OmniCampaignCardProps[]>(
    [],
  )
  const retailerAccount = useSelector(currentRetailerAccountSelector)
  const [isBusy, setisBusy] = useState(false)

  const [getContentDiscovery, { data, refetch, called }] =
    useLazyQuery<Query>(CONTENT_DISCOVERY)
  const [createContentDiscoveryCampaign] = useMutation<Mutation>(
    CREATE_CONTENT_DISCOVERY_CAMPAIGN,
  )
  const isShareFlowUpliftEnabled = useFeatureFlag('share-flow-uplift')
  const history = useHistory()

  const defaultRetailerIds = retailerAccount.retailers?.map(
    (retailer: Retailer) => {
      if (brandFilter === 'brand') {
        return !retailer.brand?.community ? retailer.id : ''
      } else if (brandFilter === 'industry') {
        return retailer.brand?.community ? retailer.id : ''
      }
      return retailer.id
    },
  )

  const { previouslySelectedRetailers, setPreviouslySelectedRetailer } =
    usePreviouslySelectedBrand(undefined, brandFilter)

  const { filterInfo, updateFilter, setPage, resetFilter, isLoading } =
    useFilter('content.content_discovery', {
      shouldForceRunOnMount: true,
      defaultFilterInfo: {
        filter: {
          filterByChannel: [],
          brands: previouslySelectedRetailers.map((x) => x.id),
        },
        pageSize,
      },
      onChange(filterInfo) {
        let variables = {
          resourceId: retailerId ? retailerId : retailerAccount.id,
          resourceType: retailerId ? 'retailers' : 'retailer_accounts',
          params: {
            per_page:
              !called && filterInfo.offset
                ? filterInfo.offset + pageSize
                : pageSize,
            offset: !called && filterInfo.offset ? 0 : filterInfo.offset,
            sort_column: 'content_discovery_items.content_created_at',
            sort_direction: 'desc',
            filterParams: {
              channel_type: [],
              enabled: [1],
            },
            fields: ['brand'],
            retailer_ids: defaultRetailerIds,
          },
        }
        if (filterInfo.filter.brands && filterInfo.filter.brands.length) {
          variables.params.retailer_ids = filterInfo.filter.brands
        }
        if (
          filterInfo.filter.filterByChannel?.length &&
          filterInfo.filter.filterByChannel[0]
        ) {
          variables.params.filterParams.channel_type =
            filterInfo.filter.filterByChannel
        }

        return (
          called ? refetch(variables) : getContentDiscovery({ variables })
        )
          .then((response) => {
            return {
              filterInfo: {
                totalResults:
                  response?.data?.getContentDiscovery?.paging?.total_results ||
                  0,
              },
            }
          })
          .catch((error) => {
            console.error(error)
          })
      },
      store: urlParamStore,
    })

  useEffect(() => {
    setPreviouslySelectedRetailer(filterInfo.filter.brands)
  }, [filterInfo.filter.brands, setPreviouslySelectedRetailer])

  useEffect(() => {
    if (!isLoading && data?.getContentDiscovery?.data) {
      // Normalize the data we get back into results
      const results =
        data?.getContentDiscovery?.data?.map((campaign) => {
          const retailer = retailerAccount.retailers.find(
            (retailer: Retailer) =>
              String(retailer.brand_id) === String(campaign?.brand_id),
          )
          const onViewOrActivate: React.MouseEventHandler = (event) => {
            event.preventDefault()
            setisBusy(true)

            return createContentDiscoveryCampaign({
              variables: {
                contentDiscoveryId: campaign?.id,
                retailerId: retailer.id,
              },
            })
              .then((response) => {
                if (isShareFlowUpliftEnabled) {
                  history.push(
                    organicCampaignPaths.details.build({
                      retailerAccountSlug: retailerAccount.slug,
                      campaignId:
                        response.data?.createContentDiscoveryCampaign
                          .creative_path || '',
                    }),
                  )
                } else {
                  // this might need additional params to be passed on back navigation for "back to content" r1 button
                  window.location.href =
                    organicCampaignPaths.legacyDetails.build({
                      retailerAccountSlug: retailerAccount.slug,
                      brandSlug: campaign?.brand?.slug || '',
                      campaignId:
                        response.data?.createContentDiscoveryCampaign
                          .creative_path || '',
                    })
                }
              })
              .catch((error) => {
                console.error(error)
              })
              .finally(() => {
                setisBusy(false)
              })
          }

          const omniCampaignCard: OmniCampaignCardProps = {
            id: campaign.id,
            title: campaign.title || '',
            subheader: '',

            description: (
              <CampaignCardTruncatedDescription>
                {campaign.description}
              </CampaignCardTruncatedDescription>
            ),

            // href: activateUrl,
            // isLegacyLink: true,
            onClick: onViewOrActivate,

            actionAreaLabel: (
              <>
                <CampaignCardActionAreaDot />

                <span>{I18n.t('content.activateCampaign')}</span>
              </>
            ),
            actionAreaColor: 'pbxxCyan',

            media: [{ publicId: campaign.image_url }],
            brandLogo: campaign?.brand?.logo_url,

            secondaryContent: (
              <>
                <PlatformAvatarGroup
                  platforms={[
                    'facebook',
                    'instagram',
                    'twitter',
                    'downloadables',
                  ]}
                />
              </>
            ),
          }

          return omniCampaignCard
        }) || []
      setCampaignCards((prev) => [...prev, ...results])
    }
  }, [
    isLoading,
    data,
    retailerAccount,
    createContentDiscoveryCampaign,
    isShareFlowUpliftEnabled,
    history,
  ])

  const clearAndReset = () => {
    setPreviouslySelectedRetailer([])
    resetFilter()
    setCampaignCards([])
  }

  useOnRetailerAccountChange(clearAndReset)

  return filterBarRef ? (
    <>
      {createPortal(
        <ContentFilters
          brandFilter={brandFilter}
          filterInfo={filterInfo}
          onClear={clearAndReset}
          hideFilters={['search', 'tags', 'show', 'filterByChannel']}
          onChange={(key, value) => {
            updateFilter({ [key]: value })
            setCampaignCards([])
          }}
        />,
        filterBarRef,
        'organic-campaigns-filter-bar',
      )}

      <CampaignResultsContainer
        pageSize={pageSize}
        isLoading={isLoading || isBusy}
        results={campaignCards}
        showMoreHandler={() => {
          setPage(filterInfo.page + 1)
        }}
        resetFilters={clearAndReset}
        totalResults={filterInfo.totalResults}
        noResultsText={I18n.t('content.noContentDiscoveryFound')}
      />
    </>
  ) : null
}

export default CampaignContentDiscovery
